import type {
  PartyRecapControversialCategory,
  PartyRecapUnanimousMiss,
  PartyRecapBiggestUpset,
  PartyRecapConsensusCategory,
  PartyRecapImpact,
  PartyRecapInsightsPayload,
  PartyRecapLeaderboardPlayer,
  PartyRecapSwing,
  PartyRecapTimeline,
} from "./types";
import { assignDenseRanks } from "./utils";

export type { PartyRecapImpact };

export interface PartyRecapNomineeInput {
  id: string;
  name: string;
}

export interface PartyRecapCategoryInput {
  id: string;
  name: string;
  fieldName: string | null;
  nominees: PartyRecapNomineeInput[];
  resultNomineeIds: string[];
  announcedAt: string | null;
}

export interface PartyRecapParticipantInput {
  userId: string;
  userName: string;
  isInvited: boolean;
}

export interface PartyRecapLeaderboardInput {
  userId: string;
  userName: string | null;
  correctCount: number;
  totalCategories: number;
  rank: number;
  isInvited: boolean;
}

export interface BuildPartyRecapInsightsInput {
  partyName: string;
  awardShowName: string;
  awardShowYear: number;
  categories: PartyRecapCategoryInput[];
  participants: PartyRecapParticipantInput[];
  leaderboard: PartyRecapLeaderboardInput[];
  userVotesMap: Record<string, Record<string, string>>;
}

// These aliases used to be standalone interfaces duplicating the shared API
// contract types; they now point at the single source of truth in
// @award-party/shared while keeping the local names stable for importers.
export type PartyRecapConsensusHighlight = PartyRecapConsensusCategory;

export type PartyRecapPlayerInsight = PartyRecapLeaderboardPlayer;

export type PartyRecapSwingInsight = PartyRecapSwing;

export type PartyRecapInsights = PartyRecapInsightsPayload;

export function buildPartyRecapInsights(
  input: BuildPartyRecapInsightsInput,
): PartyRecapInsights {
  const participantNameById = new Map(
    input.participants.map((participant) => [
      participant.userId,
      participant.userName,
    ]),
  );

  for (const entry of input.leaderboard) {
    if (!participantNameById.has(entry.userId)) {
      participantNameById.set(
        entry.userId,
        entry.userName ?? `Player ${participantNameById.size + 1}`,
      );
    }
  }

  const nomineeNameById = new Map<string, string>();
  for (const category of input.categories) {
    for (const nominee of category.nominees) {
      nomineeNameById.set(nominee.id, nominee.name);
    }
  }

  const resolvedCategories = input.categories.filter(
    (category) => category.resultNomineeIds.length > 0,
  );

  const consensusHighlights = resolvedCategories
    .map((category) => {
      const voteCounts = new Map<string, number>();

      for (const [userId, votesByCategory] of Object.entries(
        input.userVotesMap,
      )) {
        const votedNomineeId = votesByCategory[category.id];
        if (!votedNomineeId) {
          continue;
        }

        voteCounts.set(
          votedNomineeId,
          (voteCounts.get(votedNomineeId) ?? 0) + 1,
        );

        if (!participantNameById.has(userId)) {
          participantNameById.set(
            userId,
            `Player ${participantNameById.size + 1}`,
          );
        }
      }

      const totalVotes = Array.from(voteCounts.values()).reduce(
        (sum, count) => sum + count,
        0,
      );
      const highestVoteCount =
        voteCounts.size > 0 ? Math.max(...voteCounts.values()) : 0;
      const topNomineeIds = Array.from(voteCounts.entries())
        .filter(([, count]) => count === highestVoteCount && count > 0)
        .map(([nomineeId]) => nomineeId)
        .sort((left, right) => {
          const leftName = nomineeNameById.get(left) ?? left;
          const rightName = nomineeNameById.get(right) ?? right;
          return leftName.localeCompare(rightName);
        });

      const winnerNomineeIds = [...category.resultNomineeIds].sort((left, right) => {
        const leftName = nomineeNameById.get(left) ?? left;
        const rightName = nomineeNameById.get(right) ?? right;
        return leftName.localeCompare(rightName);
      });
      const winnerName = winnerNomineeIds
        .map((nomineeId) => nomineeNameById.get(nomineeId) ?? nomineeId)
        .join(" / ");
      const correctVoterNames = Object.entries(input.userVotesMap)
        .filter(
          ([, votesByCategory]) =>
            winnerNomineeIds.includes(votesByCategory[category.id] ?? ""),
        )
        .map(([userId]) => participantNameById.get(userId) ?? "Player")
        .sort((left, right) => left.localeCompare(right));

      const winnerPickedByCount = winnerNomineeIds.reduce(
        (sum, nomineeId) => sum + (voteCounts.get(nomineeId) ?? 0),
        0,
      );
      const isTie = topNomineeIds.length > 1;
      const wasConsensusWrong =
        totalVotes > 0 &&
        topNomineeIds.length > 0 &&
        !topNomineeIds.some((nomineeId) => winnerNomineeIds.includes(nomineeId));
      const firstTopNomineeId = topNomineeIds[0];
      const consensusPickName =
        topNomineeIds.length === 0
          ? null
          : topNomineeIds.length === 1
            ? firstTopNomineeId
              ? (nomineeNameById.get(firstTopNomineeId) ?? null)
              : null
            : topNomineeIds.some((nomineeId) => winnerNomineeIds.includes(nomineeId))
              ? winnerName
              : topNomineeIds
                  .map(
                    (nomineeId) => nomineeNameById.get(nomineeId) ?? nomineeId,
                  )
                  .join(" / ");

      return {
        categoryId: category.id,
        categoryName: category.name,
        winnerName,
        consensusPickName,
        pickedByCount: highestVoteCount,
        totalVotes,
        winnerPickedByCount,
        correctVoterNames,
        wasConsensusWrong,
        isTie,
      };
    })
    .filter((highlight) => highlight.totalVotes > 0);

  const scoresByUserId = new Map(
    input.leaderboard.map((entry) => [entry.userId, entry.correctCount]),
  );
  const orderedUserIds = input.leaderboard.map((entry) => entry.userId);
  const currentRankByUserId = new Map(
    input.leaderboard.map((entry) => [entry.userId, entry.rank]),
  );

  const categoryById = new Map(
    input.categories.map((category) => [category.id, category]),
  );

  const leaderboard = input.leaderboard.map((entry) => {
    const correctCategories = resolvedCategories.filter((category) => {
      const votedNomineeId = input.userVotesMap[entry.userId]?.[category.id];
      return (
        votedNomineeId !== undefined &&
        category.resultNomineeIds.includes(votedNomineeId)
      );
    });

    const fieldCounts = new Map<string, number>();
    for (const category of correctCategories) {
      const normalizedFieldName = category.fieldName?.trim();
      if (!normalizedFieldName) {
        continue;
      }

      fieldCounts.set(
        normalizedFieldName,
        (fieldCounts.get(normalizedFieldName) ?? 0) + 1,
      );
    }

    const bestFieldEntry = Array.from(fieldCounts.entries()).sort(
      (left, right) => {
        if (right[1] !== left[1]) {
          return right[1] - left[1];
        }

        return left[0].localeCompare(right[0]);
      },
    )[0];

    return {
      userId: entry.userId,
      userName:
        participantNameById.get(entry.userId) ??
        entry.userName ??
        `Player ${participantNameById.size + 1}`,
      correctCount: entry.correctCount,
      totalCategories: entry.totalCategories,
      rank: entry.rank,
      accuracy:
        entry.totalCategories > 0
          ? entry.correctCount / entry.totalCategories
          : 0,
      bestFieldName: bestFieldEntry?.[0] ?? null,
      bestFieldCorrectCount: bestFieldEntry?.[1] ?? 0,
      correctCategoryNames: correctCategories.map((category) => category.name),
      exclusiveHitCategoryNames: correctCategories
        .filter((category) => {
          const consensus = consensusHighlights.find(
            (highlight) => highlight.categoryId === category.id,
          );

          return (consensus?.correctVoterNames.length ?? 0) === 1;
        })
        .map((category) => category.name),
    };
  });

  const currentScores = Array.from(scoresByUserId.values());
  const currentLowestScore =
    currentScores.length > 0 ? Math.min(...currentScores) : 0;

  let biggestSwing: PartyRecapSwingInsight | null = null;
  let biggestSwingPriority = -1;
  let biggestSwingRankDelta = -1;
  let biggestSwingCorrectVoterCount = Number.POSITIVE_INFINITY;

  for (const highlight of consensusHighlights) {
    const category = categoryById.get(highlight.categoryId);
    if (!category || category.resultNomineeIds.length === 0) {
      continue;
    }

    for (const [userId, votesByCategory] of Object.entries(
      input.userVotesMap,
    )) {
      const votedNomineeId = votesByCategory[highlight.categoryId];
      if (!votedNomineeId || !category.resultNomineeIds.includes(votedNomineeId)) {
        continue;
      }

      const scoreWithCategory = scoresByUserId.get(userId) ?? 0;
      const scoreWithoutCategory = Math.max(scoreWithCategory - 1, 0);
      const adjustedScores = new Map(scoresByUserId);
      adjustedScores.set(userId, scoreWithoutCategory);

      const adjustedRanks = rankUsers(adjustedScores, orderedUserIds);
      const adjustedLowestScore = Math.min(...adjustedScores.values());
      const rankWithCategory =
        currentRankByUserId.get(userId) ?? orderedUserIds.length;
      const rankWithoutCategory =
        adjustedRanks.get(userId) ?? orderedUserIds.length;
      const rankDelta = Math.max(rankWithoutCategory - rankWithCategory, 0);

      const impact =
        scoreWithoutCategory === adjustedLowestScore &&
        scoreWithCategory > currentLowestScore
          ? "saved_from_last"
          : rankWithCategory === 1 && rankWithoutCategory > 1
            ? "took_first"
            : rankWithCategory <= 3 && rankWithoutCategory > 3
              ? "jumped_podium"
              : rankWithoutCategory > rankWithCategory
                ? "climbed_rankings"
                : null;

      if (!impact) {
        continue;
      }

      const impactPriority = getImpactPriority(impact);
      const shouldReplace =
        impactPriority > biggestSwingPriority ||
        (impactPriority === biggestSwingPriority &&
          rankDelta > biggestSwingRankDelta) ||
        (impactPriority === biggestSwingPriority &&
          rankDelta === biggestSwingRankDelta &&
          highlight.correctVoterNames.length < biggestSwingCorrectVoterCount);

      if (!shouldReplace) {
        continue;
      }

      biggestSwingPriority = impactPriority;
      biggestSwingRankDelta = rankDelta;
      biggestSwingCorrectVoterCount = highlight.correctVoterNames.length;
      biggestSwing = {
        userId,
        userName: participantNameById.get(userId) ?? "Player",
        categoryId: highlight.categoryId,
        categoryName: highlight.categoryName,
        winnerName: highlight.winnerName,
        correctVoterCount: highlight.correctVoterNames.length,
        rankWithCategory,
        rankWithoutCategory,
        scoreWithCategory,
        scoreWithoutCategory,
        impact,
      };
    }
  }

  const sortedHighlights = consensusHighlights.sort((left, right) => {
    if (left.wasConsensusWrong !== right.wasConsensusWrong) {
      return left.wasConsensusWrong ? -1 : 1;
    }
    if (left.correctVoterNames.length !== right.correctVoterNames.length) {
      return left.correctVoterNames.length - right.correctVoterNames.length;
    }
    return left.categoryName.localeCompare(right.categoryName);
  });

  const mostControversialCategory = computeMostControversial(
    consensusHighlights,
    nomineeNameById,
    input,
  );
  const mostUnanimousMiss = computeMostUnanimousMiss(consensusHighlights);
  const biggestUpsetInsight = computeBiggestUpset(consensusHighlights);

  const timeline = computeLeaderboardTimeline(
    input,
    participantNameById,
    nomineeNameById,
  );

  return {
    partyName: input.partyName,
    awardShowName: input.awardShowName,
    awardShowYear: input.awardShowYear,
    participantCount: input.participants.length,
    totalCategories: input.categories.length,
    resolvedCategoryCount: resolvedCategories.length,
    consensusWrongCount: sortedHighlights.filter(
      (highlight) => highlight.wasConsensusWrong,
    ).length,
    consensusHighlights: sortedHighlights,
    leaderboard,
    topPerformer: leaderboard[0] ?? null,
    bottomPerformer: leaderboard.at(-1) ?? null,
    biggestSwing,
    mostControversialCategory,
    mostUnanimousMiss,
    biggestUpset: biggestUpsetInsight,
    timeline,
  };
}

function rankUsers(
  scoresByUserId: Map<string, number>,
  orderedUserIds: string[],
): Map<string, number> {
  const sortedEntries = assignDenseRanks(
    [...orderedUserIds]
    .map((userId, index) => ({
      userId,
      score: scoresByUserId.get(userId) ?? 0,
      index,
    }))
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      return left.index - right.index;
    }),
    (entry) => entry.score,
  );

  return new Map(
    sortedEntries.map((entry) => [entry.userId, entry.rank]),
  );
}

function getImpactPriority(impact: PartyRecapImpact): number {
  switch (impact) {
    case "saved_from_last":
      return 4;
    case "took_first":
      return 3;
    case "jumped_podium":
      return 2;
    case "climbed_rankings":
      return 1;
  }
}

function computeMostControversial(
  highlights: PartyRecapConsensusHighlight[],
  nomineeNameById: Map<string, string>,
  input: BuildPartyRecapInsightsInput,
): PartyRecapControversialCategory | null {
  let best: PartyRecapControversialCategory | null = null;
  let bestMargin = Infinity;

  for (const category of input.categories) {
    if (category.resultNomineeIds.length === 0) continue;

    const voteCounts = new Map<string, number>();
    for (const votesByCategory of Object.values(input.userVotesMap)) {
      const votedId = votesByCategory[category.id];
      if (votedId) voteCounts.set(votedId, (voteCounts.get(votedId) ?? 0) + 1);
    }

    const totalVotes = Array.from(voteCounts.values()).reduce(
      (s, c) => s + c,
      0,
    );
    if (totalVotes < 2) continue;

    const sorted = Array.from(voteCounts.entries()).sort((a, b) => b[1] - a[1]);
    if (sorted.length < 2) continue;

    const margin = sorted[0]![1] - sorted[1]![1];
    if (margin < bestMargin) {
      bestMargin = margin;
      best = {
        categoryId: category.id,
        categoryName: category.name,
        topNomineeName: nomineeNameById.get(sorted[0]![0]) ?? "Unknown",
        topNomineeVotes: sorted[0]![1],
        runnerUpNomineeName: nomineeNameById.get(sorted[1]![0]) ?? "Unknown",
        runnerUpVotes: sorted[1]![1],
        totalVotes,
      };
    }
  }

  return best;
}

function computeMostUnanimousMiss(
  highlights: PartyRecapConsensusHighlight[],
): PartyRecapUnanimousMiss | null {
  let best: PartyRecapUnanimousMiss | null = null;
  let bestRatio = 0;

  for (const h of highlights) {
    if (!h.wasConsensusWrong || !h.consensusPickName || h.totalVotes === 0)
      continue;

    const ratio = h.pickedByCount / h.totalVotes;
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = {
        categoryId: h.categoryId,
        categoryName: h.categoryName,
        consensusPickName: h.consensusPickName,
        winnerName: h.winnerName,
        unanimousVoteCount: h.pickedByCount,
        totalVotes: h.totalVotes,
      };
    }
  }

  return best;
}

function computeBiggestUpset(
  highlights: PartyRecapConsensusHighlight[],
): PartyRecapBiggestUpset | null {
  let best: PartyRecapBiggestUpset | null = null;
  let lowestWinnerRatio = Infinity;

  for (const h of highlights) {
    if (h.totalVotes === 0) continue;

    const ratio = h.winnerPickedByCount / h.totalVotes;
    if (ratio < lowestWinnerRatio) {
      lowestWinnerRatio = ratio;
      best = {
        categoryId: h.categoryId,
        categoryName: h.categoryName,
        winnerName: h.winnerName,
        winnerVoteCount: h.winnerPickedByCount,
        totalVotes: h.totalVotes,
      };
    }
  }

  return best;
}

function computeLeaderboardTimeline(
  input: BuildPartyRecapInsightsInput,
  participantNameById: Map<string, string>,
  nomineeNameById: Map<string, string>,
): PartyRecapTimeline | null {
  const resolvedWithTimestamps = input.categories.filter(
    (c) => c.resultNomineeIds.length > 0 && c.announcedAt,
  );

  if (resolvedWithTimestamps.length < 2) return null;

  const uniqueTimestamps = new Set(
    resolvedWithTimestamps.map((c) => c.announcedAt),
  );
  if (uniqueTimestamps.size < 2) return null;

  const sorted = [...resolvedWithTimestamps].sort(
    (a, b) =>
      new Date(a.announcedAt!).getTime() - new Date(b.announcedAt!).getTime(),
  );

  const orderedUserIds = input.leaderboard.map((e) => e.userId);
  const runningScores = new Map<string, number>(
    orderedUserIds.map((id) => [id, 0]),
  );

  let leadChanges = 0;
  let previousLeader: string | null = null;

  const leaderStreaks = new Map<string, number>();
  let currentStreakLeader: string | null = null;
  let currentStreakLength = 0;

  const worstRankPerUser = new Map<
    string,
    { rank: number; afterCategory: string }
  >();
  let finalRanks = new Map<string, number>();

  let crowningCategory: PartyRecapTimeline["crowningCategory"] = null;
  const finalWinnerUserId = input.leaderboard[0]?.userId;

  for (const category of sorted) {
    for (const [userId, votesByCategory] of Object.entries(
      input.userVotesMap,
    )) {
      const votedNomineeId = votesByCategory[category.id];
      if (
        votedNomineeId &&
        category.resultNomineeIds.includes(votedNomineeId)
      ) {
        runningScores.set(userId, (runningScores.get(userId) ?? 0) + 1);
      }
    }

    const ranks = rankUsers(runningScores, orderedUserIds);
    finalRanks = ranks;

    let currentLeader: string | null = null;
    for (const [userId, rank] of ranks) {
      if (rank === 1) {
        currentLeader = userId;
        break;
      }
    }

    if (
      currentLeader &&
      currentLeader !== previousLeader &&
      previousLeader !== null
    ) {
      leadChanges++;
    }

    if (currentLeader === currentStreakLeader) {
      currentStreakLength++;
    } else {
      if (currentStreakLeader) {
        const prev = leaderStreaks.get(currentStreakLeader) ?? 0;
        if (currentStreakLength > prev) {
          leaderStreaks.set(currentStreakLeader, currentStreakLength);
        }
      }
      currentStreakLeader = currentLeader;
      currentStreakLength = 1;
    }

    previousLeader = currentLeader;

    for (const [userId, rank] of ranks) {
      const existing = worstRankPerUser.get(userId);
      if (!existing || rank > existing.rank) {
        worstRankPerUser.set(userId, { rank, afterCategory: category.name });
      }
    }

    if (finalWinnerUserId && currentLeader === finalWinnerUserId) {
      crowningCategory = {
        categoryName: category.name,
        winnerName: category.resultNomineeIds
          .map((nomineeId) => nomineeNameById.get(nomineeId) ?? nomineeId)
          .join(" / "),
        userId: finalWinnerUserId,
        userName: participantNameById.get(finalWinnerUserId) ?? "Player",
      };
    }
  }

  if (currentStreakLeader) {
    const prev = leaderStreaks.get(currentStreakLeader) ?? 0;
    if (currentStreakLength > prev) {
      leaderStreaks.set(currentStreakLeader, currentStreakLength);
    }
  }

  let longestLeader: PartyRecapTimeline["longestLeader"] = null;
  let maxStreak = 0;
  for (const [userId, streak] of leaderStreaks) {
    if (streak > maxStreak) {
      maxStreak = streak;
      longestLeader = {
        userId,
        userName: participantNameById.get(userId) ?? "Player",
        categoriesInLead: streak,
      };
    }
  }

  let biggestComeback: PartyRecapTimeline["biggestComeback"] = null;
  let maxComebackDelta = 0;
  for (const [userId, worst] of worstRankPerUser) {
    const finalRank = finalRanks.get(userId) ?? worst.rank;
    const delta = worst.rank - finalRank;
    if (delta > maxComebackDelta && finalRank < worst.rank) {
      maxComebackDelta = delta;
      biggestComeback = {
        userId,
        userName: participantNameById.get(userId) ?? "Player",
        worstRank: worst.rank,
        finalRank,
        worstAfterCategory: worst.afterCategory,
      };
    }
  }

  return { leadChanges, longestLeader, biggestComeback, crowningCategory };
}
